import {
  ArrowDropDown,
  ArrowDropUp,
  ArrowLeft,
  ArrowRight,
  HelpOutline,
  VisibilityOff,
  Visibility,
  ZoomIn,
  ZoomOut,
  Close,
} from '@mui/icons-material'
import { Button, Typography } from '@mui/material'
import { TransformComponent } from 'react-zoom-pan-pinch'
import classNames from 'classnames'

import TrayMapSelector from 'components/organisms/TrayMapSelector/TrayMapSelector'
import {
  TrayMapWithControlsProps,
  useTrayMapControls,
  useLegendControls,
  getControlAccessibilityProps,
} from './TrayMapWithControls.logic'

const TrayMapWithControls = ({
  isInEditMode,
  isLoadingWithUDIs,
  trayType,
  trayImg,
  isLegendOpen,
  setIsLegendOpen,
}: TrayMapWithControlsProps) => {
  const {
    areControlsVisible,
    isMobile,
    handlePan,
    handleZoomIn,
    handleZoomOut,
    toggleControlsVisibility,
  } = useTrayMapControls()

  const { toggleLegend, openLegend } = useLegendControls(
    isLegendOpen,
    setIsLegendOpen
  )
  const accessibilityProps = getControlAccessibilityProps(areControlsVisible)

  const renderMobileControls = () => (
    <>
      <div className="mobile-map-header">
        <div className="mobile-title">Digital Tray Map</div>
      </div>
      <div className="mobile-map-actions-bar">
        <button
          className="icon-btn"
          aria-label={accessibilityProps.legendButton.ariaLabel}
          title={accessibilityProps.legendButton.title}
          onClick={toggleLegend}
        >
          <HelpOutline />
        </button>
        <button
          className="icon-btn"
          aria-label={accessibilityProps.controlsButton.ariaLabel}
          title={accessibilityProps.controlsButton.title}
          onClick={toggleControlsVisibility}
        >
          {areControlsVisible ? <VisibilityOff /> : <Visibility />}
        </button>
      </div>
      {areControlsVisible && (
        <div className="mobile-map-controls-bar">
          <button
            className="hide-controls-btn"
            aria-label="Hide map controls"
            title="Hide map controls"
            onClick={() => toggleControlsVisibility()}
          >
            <Close style={{ fontSize: 24, color: '#333' }} />
          </button>
          <div className="dpad-row">
            <button
              className="map-control-btn"
              onClick={() => handlePan('up')}
              aria-label={accessibilityProps.panButtons.up.ariaLabel}
            >
              <ArrowDropUp />
            </button>
          </div>
          <div className="dpad-center">
            <button
              className="map-control-btn"
              onClick={() => handlePan('left')}
              aria-label={accessibilityProps.panButtons.left.ariaLabel}
            >
              <ArrowLeft />
            </button>
            <div style={{ width: 48 }} />
            <button
              className="map-control-btn"
              onClick={() => handlePan('right')}
              aria-label={accessibilityProps.panButtons.right.ariaLabel}
            >
              <ArrowRight />
            </button>
          </div>
          <div className="dpad-row">
            <button
              className="map-control-btn"
              onClick={() => handlePan('down')}
              aria-label={accessibilityProps.panButtons.down.ariaLabel}
            >
              <ArrowDropDown />
            </button>
          </div>
          <div className="zoom-row">
            <button
              className="map-control-btn"
              onClick={handleZoomOut}
              aria-label={accessibilityProps.zoomButtons.out.ariaLabel}
            >
              <ZoomOut />
            </button>
            <button
              className="map-control-btn"
              onClick={handleZoomIn}
              aria-label={accessibilityProps.zoomButtons.in.ariaLabel}
            >
              <ZoomIn />
            </button>
          </div>
        </div>
      )}
    </>
  )

  const renderDesktopHeader = () => (
    <div className="panel-header">
      <Typography variant="h6">Digital Tray Map</Typography>
      <Button
        className="legend-toggle-button"
        onClick={openLegend}
        aria-label="Show Legend"
      >
        <HelpOutline />
      </Button>
    </div>
  )

  return (
    <div
      className={`map-container ${
        areControlsVisible ? 'controls-visible' : ''
      }`}
    >
      {isMobile ? renderMobileControls() : renderDesktopHeader()}

      <div className="tray-map_container">
        <TransformComponent
          contentClass="tray-map-content"
          wrapperClass="tray-map-wrapper"
        >
          <div
            className={classNames('map-selector-container', {
              disabled: !isInEditMode,
            })}
          >
            <TrayMapSelector
              isSPD={isLoadingWithUDIs}
              trayType={trayType}
              trayImg={trayImg}
            />
          </div>
        </TransformComponent>
      </div>
    </div>
  )
}

export default TrayMapWithControls
